import React, { useState, useEffect, useMemo, useCallback } from 'react';
import supabase from '../services/supabase';
import { useAuth } from '../context/AuthContext';
import { useFeatureContext } from '../context/FeatureContext';
import { listBooks, toggleBookFavorite } from '../services/bookService';
import { getScholenLijst, kiesSchool } from '../services/onboardingService';
import OnboardingTour, { TOUR_KEY } from './OnboardingTour';
import './VakkenOnboarding.css';

// Vakken in secties, aansluitend op de vakken van de boekencatalogus
// (o.a. NaSk als eigen vak en Wiskunde A/B/C voor de bovenbouw).
const VAK_SECTIES = [
  { titel: 'Kernvakken', vakken: ['Nederlands', 'Engels', 'Wiskunde', 'Wiskunde A', 'Wiskunde B', 'Wiskunde C'] },
  { titel: 'Bètavakken', vakken: ['NaSk', 'Natuurkunde', 'Scheikunde', 'Biologie', 'Informatica', 'NLT', 'Techniek'] },
  { titel: 'Moderne vreemde talen', vakken: ['Duits', 'Frans', 'Spaans'] },
  { titel: 'Mens & maatschappij', vakken: ['Geschiedenis', 'Aardrijkskunde', 'Economie', 'Bedrijfseconomie', 'Maatschappijleer', 'Maatschappijwetenschappen', 'Filosofie', 'Godsdienst'] },
  { titel: 'Kunst & cultuur', vakken: ['Kunst', 'Muziek', 'Drama', 'Tekenen', 'Handvaardigheid', 'CKV'] },
  { titel: 'Klassieke talen', vakken: ['Latijn', 'Grieks'] },
  { titel: 'Overig', vakken: ['Lichamelijke opvoeding', 'BSM', 'Management & Organisatie'] },
];
const ALLE_VAKKEN = VAK_SECTIES.flatMap(s => s.vakken);

// Docent-vak → books.subject-waarden (identiteit is de fallback).
// De catalogus kent o.a. "NaSk" dat niet in ALLE_VAKKEN voorkomt.
const VAK_SUBJECT_ALIASES = {
  NaSk: ['NaSk', 'Natuurkunde', 'Scheikunde'],
  Natuurkunde: ['Natuurkunde', 'NaSk'],
  Scheikunde: ['Scheikunde', 'NaSk'],
  Techniek: ['NaSk'],
  NLT: ['Natuurkunde', 'Scheikunde', 'Biologie', 'NaSk'],
  'Wiskunde A': ['Wiskunde'],
  'Wiskunde B': ['Wiskunde'],
  'Wiskunde C': ['Wiskunde'],
};

const subjectsVoor = (vakken) =>
  new Set(vakken.flatMap(v => VAK_SUBJECT_ALIASES[v] ?? [v]));

const STAP_LABELS = { welkom: 'Welkom', school: 'School', vakken: 'Vakken', boeken: 'Boeken', rondleiding: 'Rondleiding' };

// Cover of "Geen cover" als de URL niet laadt of geen afbeelding is (PDF).
function OnboardingCover({ book }) {
  const [kapot, setKapot] = useState(false);
  if (!book.cover_image_url || kapot) return <div className="vob-book-nocover">Geen cover</div>;
  return (
    <img
      src={book.cover_image_url}
      alt={book.title}
      loading="lazy"
      onError={() => setKapot(true)}
      onLoad={(e) => { if (!e.currentTarget.naturalWidth) setKapot(true); }}
    />
  );
}

function OnboardingWizard({ onClose }) {
  const { currentUser, docentProfile, refreshDocentProfile } = useAuth();
  const featureCtx = useFeatureContext();
  const isEnabled = useCallback(
    (key) => (featureCtx ? featureCtx.isEnabled(key) : true),
    [featureCtx]
  );

  // Midden in de rondleiding her-mount elke navigatie de wizard; de tour-
  // sleutel in sessionStorage betekent: direct doorgaan met de rondleiding.
  const [step, setStep] = useState(() => {
    try { return sessionStorage.getItem(TOUR_KEY) != null ? 'rondleiding' : 'welkom'; } catch { return 'welkom'; }
  });
  const [selected, setSelected] = useState(() => docentProfile?.vakken ?? []);
  const [customVak, setCustomVak] = useState('');
  const [books, setBooks] = useState([]);
  const [booksState, setBooksState] = useState('loading');
  // Schoolkeuze (tot Entree-herkenning overal actief is)
  const [scholen, setScholen] = useState([]);
  const [scholenState, setScholenState] = useState('loading');
  const [gekozenSchool, setGekozenSchool] = useState(null);
  const [schoolGezet, setSchoolGezet] = useState(false);
  const [selectedBookIds, setSelectedBookIds] = useState(() => new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const boekenMogelijk = isEnabled('boekenplank') && !!(docentProfile?.school_id || schoolGezet);

  // Schoollijst alleen ophalen zolang het account schoolloos is.
  useEffect(() => {
    if (docentProfile?.school_id || schoolGezet) return undefined;
    let actief = true;
    getScholenLijst()
      .then(d => { if (actief) { setScholen(d.scholen || []); setScholenState('ok'); } })
      .catch(() => { if (actief) setScholenState('fout'); });
    return () => { actief = false; };
  }, [docentProfile?.school_id, schoolGezet]);

  useEffect(() => {
    if (!boekenMogelijk) { setBooksState('unavailable'); return; }
    let actief = true;
    setBooksState('loading');
    listBooks({ accessOnly: true }).then(res => {
      if (!actief) return;
      if (res.success && res.books.length > 0) {
        setBooks(res.books);
        setBooksState('ok');
      } else {
        setBooksState('unavailable');
      }
    });
    return () => { actief = false; };
  }, [boekenMogelijk]);

  // Vakken alleen vragen als we ze niet al kennen (Entree/Zermelo kan ze
  // hebben gezet); de gekozen/bekende vakken filteren daarna de boekenstap.
  const vakkenBekend = (docentProfile?.vakken ?? []).length > 0;

  const stappen = useMemo(() => {
    const s = ['welkom'];
    if (!docentProfile?.school_id && !schoolGezet) s.push('school');
    if (!vakkenBekend) s.push('vakken');
    if (booksState !== 'unavailable') s.push('boeken');
    s.push('rondleiding');
    return s;
  }, [docentProfile?.school_id, schoolGezet, vakkenBekend, booksState]);

  // Afronden of wegklikken: markering wegschrijven en sluiten. Fouten mogen
  // het sluiten nooit blokkeren — de gate houdt de wizard via sessionStorage
  // sowieso dicht voor de rest van de sessie.
  const finish = async () => {
    setSaving(true);
    try { sessionStorage.removeItem(TOUR_KEY); } catch {}
    try {
      await supabase
        .from('docenten')
        .update({ onboarding_gezien: new Date().toISOString() })
        .eq('id', currentUser.id);
      refreshDocentProfile()?.catch?.(() => {});
    } catch (err) {
      console.error('Fout bij afronden onboarding:', err);
    }
    onClose();
  };

  const volgendeNa = (huidige) => {
    const i = stappen.indexOf(huidige);
    if (i === -1 || i === stappen.length - 1) return finish();
    setError(null);
    const volgende = stappen[i + 1];
    if (volgende === 'rondleiding') {
      try { sessionStorage.setItem(TOUR_KEY, '0'); } catch {}
    }
    setStep(volgende);
  };

  const toggleVak = (vak) => {
    setSelected(prev =>
      prev.includes(vak) ? prev.filter(v => v !== vak) : [...prev, vak]
    );
  };

  const addCustomVak = () => {
    const trimmed = customVak.trim();
    if (trimmed && !selected.includes(trimmed)) {
      setSelected(prev => [...prev, trimmed]);
    }
    setCustomVak('');
  };

  const handleCustomKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addCustomVak();
    }
  };

  const saveSchoolEnVerder = async () => {
    if (!gekozenSchool) { volgendeNa('school'); return; }
    setSaving(true);
    setError(null);
    try {
      await kiesSchool(gekozenSchool);
      setSchoolGezet(true);
      refreshDocentProfile()?.catch?.(() => {});
      setSaving(false);
      volgendeNa('school');
    } catch (err) {
      console.error('Fout bij schoolkeuze:', err);
      setError('School koppelen lukte niet. Probeer het opnieuw of sla deze stap over.');
      setSaving(false);
    }
  };

  const saveVakkenEnVerder = async () => {
    if (selected.length === 0) { volgendeNa('vakken'); return; }
    setSaving(true);
    setError(null);
    try {
      const { error: updateError } = await supabase
        .from('docenten')
        .update({ vakken: selected })
        .eq('id', currentUser.id);
      if (updateError) throw updateError;
      refreshDocentProfile()?.catch?.(() => {});
      setSaving(false);
      volgendeNa('vakken');
    } catch (err) {
      console.error('Fout bij opslaan vakken:', err);
      setError('Er ging iets mis bij het opslaan. Probeer het opnieuw of sla deze stap over.');
      setSaving(false);
    }
  };

  const toggleBoek = (bookId) => {
    setSelectedBookIds(prev => {
      const next = new Set(prev);
      if (next.has(bookId)) next.delete(bookId);
      else next.add(bookId);
      return next;
    });
  };

  const saveBoekenEnVerder = async () => {
    const nieuw = [...selectedBookIds].filter(id => !books.find(b => b.id === id)?.is_favorite);
    if (nieuw.length === 0) { volgendeNa('boeken'); return; }
    setSaving(true);
    setError(null);
    try {
      await Promise.all(nieuw.map(id => toggleBookFavorite(id)));
      setSaving(false);
      volgendeNa('boeken');
    } catch (err) {
      console.error('Fout bij favoriet maken boeken:', err);
      setError('Boeken opslaan lukte niet. Je kunt ze later op de Boekenplank alsnog favoriet maken.');
      setSaving(false);
    }
  };

  // Boeken bij de gekozen vakken; geen match → volledig aanbod met melding.
  const { getoondeBoeken, boekenFallback } = useMemo(() => {
    if (selected.length === 0) return { getoondeBoeken: books, boekenFallback: false };
    const subjects = subjectsVoor(selected);
    const match = books.filter(b => subjects.has(b.subject));
    return match.length > 0
      ? { getoondeBoeken: match, boekenFallback: false }
      : { getoondeBoeken: books, boekenFallback: true };
  }, [books, selected]);

  const sterSvg = (actief) => (
    <svg width="17" height="17" viewBox="0 0 24 24" fill={actief ? '#f59e0b' : 'none'} stroke={actief ? '#f59e0b' : '#94a3b8'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
  );

  // De rondleiding wijst de echte menu-knoppen aan; de modal gaat dan dicht.
  if (step === 'rondleiding') {
    return <OnboardingTour onFinish={finish} />;
  }

  return (
    <div className="vob-overlay">
      <div className="vob-modal">
        <button className="vob-close" onClick={finish} title="Sluiten" aria-label="Onboarding sluiten">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>

        <div className="vob-steps">
          {stappen.map(s => (
            <span key={s} className={`vob-step-dot ${s === step ? 'active' : ''}`} title={STAP_LABELS[s]} />
          ))}
        </div>

        {step === 'welkom' && (
          <>
            <div className="vob-header">
              <img src="/tw-logo-04.png" alt="TestWisely" className="vob-logo" />
              <h2 className="vob-title">Welkom bij TestWisely</h2>
              <p className="vob-subtitle">
                Fijn dat je er bent. In een paar korte stappen richten we je account in:
                {vakkenBekend ? ' je kiest de boeken die je gebruikt' : ' je vertelt welke vakken je geeft, kiest de boeken die je gebruikt'} en
                daarna wijzen we je de weg door het menu. Het duurt nog geen minuut en je
                kunt alles later aanpassen.
              </p>
            </div>
            <div className="vob-footer">
              <button className="vob-skip-btn" onClick={finish} disabled={saving}>Overslaan</button>
              <button className="vob-save-btn" onClick={() => volgendeNa('welkom')} disabled={saving}>Aan de slag</button>
            </div>
          </>
        )}

        {step === 'school' && (
          <>
            <div className="vob-header">
              <h2 className="vob-title">Bij welke school geef je les?</h2>
              <p className="vob-subtitle">Kies je school zodat je klassen, boeken en lessen op de juiste plek staan. Zodra inloggen via Entree actief is, gaat dit automatisch.</p>
            </div>

            {scholenState === 'loading' && <p className="vob-note">Scholen laden...</p>}
            {scholenState === 'fout' && <p className="vob-note">De schoollijst laden lukte niet. Sla deze stap over — koppelen kan later ook via je beheerder.</p>}
            {scholenState === 'ok' && (
              <div className="vob-school-lijst">
                {scholen.map(s => (
                  <button
                    key={s.id}
                    className={`vob-school ${gekozenSchool === s.id ? 'selected' : ''}`}
                    onClick={() => setGekozenSchool(s.id)}
                  >
                    <span className="vob-school-naam">{s.naam}</span>
                    {s.plaats && <span className="vob-school-plaats">{s.plaats}</span>}
                    {gekozenSchool === s.id && (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                    )}
                  </button>
                ))}
              </div>
            )}

            <p className="vob-note">Staat je school er niet tussen? Sla deze stap over en neem contact met ons op.</p>

            {error && <div className="vob-error">{error}</div>}

            <div className="vob-footer">
              <span />
              <div className="vob-btn-row">
                <button className="vob-skip-btn" onClick={() => volgendeNa('school')} disabled={saving}>Overslaan</button>
                <button className="vob-save-btn" onClick={saveSchoolEnVerder} disabled={saving || !gekozenSchool}>
                  {saving ? 'Koppelen...' : 'Doorgaan'}
                </button>
              </div>
            </div>
          </>
        )}

        {step === 'vakken' && (
          <>
            <div className="vob-header">
              <h2 className="vob-title">Welke vakken geef je?</h2>
              <p className="vob-subtitle">Selecteer de vakken die je geeft. Je kunt dit later altijd aanpassen in Instellingen.</p>
            </div>

            <div className="vob-secties">
              {VAK_SECTIES.map(sectie => (
                <div key={sectie.titel}>
                  <div className="vob-sectie-label">{sectie.titel}</div>
                  <div className="vob-grid">
                    {sectie.vakken.map(vak => (
                      <button
                        key={vak}
                        className={`vob-chip ${selected.includes(vak) ? 'active' : ''}`}
                        onClick={() => toggleVak(vak)}
                      >
                        {selected.includes(vak) && (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                        )}
                        {vak}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Custom vakken die niet in de lijst staan */}
            {selected.filter(v => !ALLE_VAKKEN.includes(v)).length > 0 && (
              <div className="vob-custom-chips">
                {selected.filter(v => !ALLE_VAKKEN.includes(v)).map(vak => (
                  <span key={vak} className="vob-chip active">
                    {vak}
                    <button className="vob-chip-remove" onClick={() => toggleVak(vak)}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </button>
                  </span>
                ))}
              </div>
            )}

            <div className="vob-custom-row">
              <input
                className="vob-custom-input"
                placeholder="Ander vak toevoegen..."
                value={customVak}
                onChange={e => setCustomVak(e.target.value)}
                onKeyDown={handleCustomKeyDown}
              />
              <button
                className="vob-custom-btn"
                onClick={addCustomVak}
                disabled={!customVak.trim()}
              >
                Toevoegen
              </button>
            </div>

            {!docentProfile?.school_id && !schoolGezet && (
              <p className="vob-note">Boeken koppelen kan zodra je account aan een school is gekoppeld.</p>
            )}

            {error && <div className="vob-error">{error}</div>}

            <div className="vob-footer">
              <span className="vob-count">{selected.length} vak{selected.length !== 1 ? 'ken' : ''} geselecteerd</span>
              <div className="vob-btn-row">
                <button className="vob-skip-btn" onClick={() => volgendeNa('vakken')} disabled={saving}>Overslaan</button>
                <button className="vob-save-btn" onClick={saveVakkenEnVerder} disabled={saving}>
                  {saving ? 'Opslaan...' : 'Doorgaan'}
                </button>
              </div>
            </div>
          </>
        )}

        {step === 'boeken' && (
          <>
            <div className="vob-header">
              <h2 className="vob-title">Kies je lesboeken</h2>
              <p className="vob-subtitle">
                Deze boeken komen als favoriet op je Boekenplank, zodat je er direct
                toetsvragen uit kunt genereren.
              </p>
            </div>

            {booksState === 'loading' && <p className="vob-note">Boeken laden...</p>}

            {booksState === 'ok' && (
              <>
                {boekenFallback && (
                  <p className="vob-note">Geen boeken gevonden bij je vakken — hier is het volledige aanbod.</p>
                )}
                <div className="vob-book-grid">
                  {getoondeBoeken.map(book => {
                    const actief = selectedBookIds.has(book.id) || book.is_favorite;
                    return (
                      <button
                        key={book.id}
                        className={`vob-book-card ${actief ? 'selected' : ''}`}
                        onClick={() => !book.is_favorite && toggleBoek(book.id)}
                        title={book.is_favorite ? 'Staat al op je Boekenplank' : book.title}
                      >
                        <OnboardingCover key={book.cover_image_url || 'geen'} book={book} />
                        <span className="vob-book-star">{sterSvg(actief)}</span>
                        <span className="vob-book-subject">{book.subject}</span>
                        <span className="vob-book-title">{book.title}</span>
                      </button>
                    );
                  })}
                </div>
              </>
            )}

            {error && <div className="vob-error">{error}</div>}

            <div className="vob-footer">
              <span className="vob-count">
                {selectedBookIds.size} boek{selectedBookIds.size !== 1 ? 'en' : ''} gekozen
                {books.some(b => b.is_favorite) ? ` · ${books.filter(b => b.is_favorite).length} al op je plank` : ''}
              </span>
              <div className="vob-btn-row">
                <button className="vob-skip-btn" onClick={() => volgendeNa('boeken')} disabled={saving}>Overslaan</button>
                <button className="vob-save-btn" onClick={saveBoekenEnVerder} disabled={saving || booksState === 'loading'}>
                  {saving ? 'Opslaan...' : 'Doorgaan'}
                </button>
              </div>
            </div>
          </>
        )}

      </div>
    </div>
  );
}

export { ALLE_VAKKEN };
export default OnboardingWizard;
